/**
 * Check-In Trends Dashboard
 * Unified view for visualising wellness trends over time
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Brain, Sparkles, BarChart3 } from 'lucide-react';
import CheckInTrendsChart from './CheckInTrendsChart';
import TrendsInsightsSummary from './TrendsInsightsSummary';
import PatternInsightsPanel from './PatternInsightsPanel';
import { TypewriterMessage } from '@/components/onboarding/TypewriterMessage';
import { ContextualNotificationPrompt } from '@/components/notifications/ContextualNotificationPrompt';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TrendsDashboardProps {
  defaultTab?: 'trends' | 'patterns' | 'insights';
}

export default function TrendsDashboard({ defaultTab = 'trends' }: TrendsDashboardProps) {
  const { data: userId } = useQuery({
    queryKey: ['current-user-id'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || '';
    }
  });

  return (
    <div className="space-y-6">
      {/* Header with Typewriter */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
          <BarChart3 className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">Wellness Trends</h1>
          <div className="text-sm text-muted-foreground min-h-[2.5rem]">
            <TypewriterMessage 
              text="Your data tells a story. These trends reveal how different aspects of your life weave together - sleep affecting mood, movement shifting energy, connection influencing wellbeing."
              speed={18}
              showCursor={false}
            />
          </div>
        </div>
      </motion.div>

      {/* Contextual notification prompt for trends */}
      <ContextualNotificationPrompt context="trends" />

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-muted/50">
          <TabsTrigger value="trends" className="gap-2 text-xs sm:text-sm">
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Visual</span> Trends
          </TabsTrigger>
          <TabsTrigger value="patterns" className="gap-2 text-xs sm:text-sm">
            <Brain className="w-4 h-4" />
            Patterns
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2 text-xs sm:text-sm">
            <Sparkles className="w-4 h-4" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="mt-6">
          <CheckInTrendsChart />
        </TabsContent>

        <TabsContent value="patterns" className="mt-6">
          {userId && <PatternInsightsPanel userId={userId} />}
        </TabsContent>

        <TabsContent value="insights" className="mt-6">
          <TrendsInsightsSummary />
        </TabsContent>
      </Tabs>
    </div>
  );
}
